/**
 * @author Team CMS
 *
 * A video player component that supports multiple video platforms.
 */
'use client';

import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo, useRef } from 'react';
import { cn } from '../../../utils/index.js';
import { generateSrcString } from '../../core/utils/generateSrcString.js';
import { getVideoOrigin } from '../../core/utils/videoPlayerControls.js';
import { useVideoJsonLD } from './hooks/useVideoJsonLD.js';
import { useVideoMessageHandler } from './hooks/useVideoMessageHandler.js';
import { useVideoStateTracker } from './hooks/useVideoStateTracker.js';
/**
 * A video player component that supports multiple video platforms.
 *
 * @param props - Video player properties
 * @param props.video
 * @param props.height
 * @param props.width
 * @param props.className
 * @param props.autoplay
 * @param props.loading
 * @param props.enableJsApi
 * @returns - Video player component
 */
export default function VideoPlayer({
  video,
  height,
  width,
  className = '',
  autoplay = false,
  loading = 'eager',
  enableJsApi = false
}) {
  const iframeRef = useRef(null);
  // Prepare the video URL string based on the video type and other properties
  const src = useMemo(() => generateSrcString(video.type, video.videoId, enableJsApi, autoplay), [video.type, video.videoId, autoplay]);
  // Determine the video platform origin (e.g., YouTube, SolidTango, etc.)
  const videoOrigin = useMemo(() => getVideoOrigin(video.type), [video.type]);
  // Calculate aspect ratio for the video iframe
  const aspectRatio = useMemo(() => width && height ? height / width * 100 : 9 / 16 * 100, [width, height]);
  // Callback to handle iframe load event
  const handleIframeLoad = useCallback(() => {
    if (video.type === 'youtube' && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage('{"event":"listening","channel":"widget"}', '*');
    }
  }, [video.type]);
  // Register the message event listener on the client
  useVideoMessageHandler(video, videoOrigin);
  // Add JSON-LD schema for the video
  useVideoJsonLD(video);
  // Track the video player state
  useVideoStateTracker(video);
  // Prepare the iframe properties
  const iframeProps = {
    id: `video-${video.videoId}`,
    className: 'absolute left-0 top-0 h-full w-full border-none overflow-hidden',
    height,
    width,
    title: video.title || 'Video',
    src: src ?? undefined,
    loading,
    allow: video.type === 'youtube' ? 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture' : undefined,
    allowFullScreen: true,
    onLoad: handleIframeLoad
  };
  return _jsx("div", {
    className: cn('relative block w-full overflow-hidden', className),
    style: {
      paddingBottom: `${aspectRatio}%`
    },
    children: _jsx("iframe", {
      ...iframeProps,
      "data-video-type": video.type,
      ref: iframeRef
    })
  });
}